/**
 * @fileoverview Tela "Abordagens inteligentes" (admin): os fluxos de
 * acompanhamento automático da API oficial. Cada fluxo liga um template a até
 * duas mensagens que saem sozinhas quando o cliente não responde.
 *
 * - Lista com Todos/Ativos/Inativos, a sequência, a conexão, o horário de
 *   envio, quantas abordagens estão em andamento e o aviso de template pausado,
 *   desativado ou excluído na Meta.
 * - Criar e editar no diálogo (FlowEditor). Ativar e desativar no interruptor;
 *   desativar com abordagens em andamento pede confirmação (elas param).
 * - Excluir pede confirmação; o fluxo some da lista e os números continuam no
 *   Dashboard.
 * O servidor confere tudo. Com a regra nova ligada, ver a lista pede
 * smart_approaches.view e criar, ligar, editar e excluir, smart_approaches.manage;
 * sem ela, a tela só não mostra a lista a quem não é admin.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Switch, Tooltip } from "@material-ui/core";
import { AlertTriangle, Pencil, Plus, Sparkles, Trash2 } from "lucide-react";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import ConfirmationModal from "../../components/ConfirmationModal";
import { LoadError, LoadingState } from "../../components/Settings/LoadState";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermission, { isAdminUser } from "../../hooks/usePermission";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import useManagerStyles from "../Flows/components/TriageManager/styles";
import useStyles from "./styles";
import FlowEditor from "./FlowEditor";
import { sequenceText } from "./flowForm";
import { windowText } from "../../utils/smartApproachText";
import { showSaveError } from "./apiErrors";

const t = (key, params) => i18n.t(`smartApproaches.${key}`, params);

const FILTERS = ["all", "active", "inactive"];

const sequenceLabel = (key, params) => t(`sequence.${key}`, params);

const issueStatus = (status) => {
  const known = ["PAUSED", "DISABLED", "REJECTED", "PENDING", "IN_APPEAL", "MISSING"];
  return known.includes(status) ? t(`issues.status.${status}`) : t("issues.status.other");
};

const issueStep = (step) => (step === 1 ? t("issues.initial") : t("issues.step", { n: step }));

const SmartApproaches = () => {
  const classes = useManagerStyles();
  const own = useStyles();
  const { user } = useContext(AuthContext);
  const { whatsApps, loading: connectionsLoading } = useContext(WhatsAppsContext);
  const { can } = usePermission();
  const canView = can("smart_approaches.view", isAdminUser(user));
  const canManage = can("smart_approaches.manage", isAdminUser(user));
  const connections = useMemo(() => (whatsApps || []).filter((w) => w.provider === "meta-api"), [whatsApps]);

  const [flows, setFlows] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = (requestSeq.current += 1);
    setLoadFailed(false);
    try {
      const { data } = await api.get("/smart-approaches");
      if (seq === requestSeq.current) setFlows(Array.isArray(data) ? data : []);
    } catch (err) {
      // Sem a lista, nada de "nenhum fluxo ainda": o erro com "Tentar de novo".
      if (seq === requestSeq.current) setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    if (canView) load();
  }, [canView, load]);

  const counts = useMemo(() => {
    const list = flows || [];
    return { all: list.length, active: list.filter((f) => f.active).length, inactive: list.filter((f) => !f.active).length };
  }, [flows]);
  const visible = useMemo(
    () => (flows || []).filter((f) => filter === "all" || (filter === "active" ? f.active : !f.active)),
    [flows, filter]
  );

  const setActive = async (flow, active) => {
    setBusy(true);
    try {
      await api.patch(`/smart-approaches/${flow.id}/active`, { active });
      toast.success(active ? t("activated") : t("deactivated"));
      await load();
    } catch (err) {
      showSaveError(err);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const toggle = (flow, active) => {
    if (!active && flow.openRuns > 0) setConfirm({ kind: "deactivate", flow });
    else setActive(flow, active);
  };

  const remove = async (flow) => {
    setBusy(true);
    try {
      await api.delete(`/smart-approaches/${flow.id}`);
      toast.success(t("deleted"));
      await load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const renderIssues = (flow) => {
    if (flow.templateIssues === null) return <div className={classes.hint}>{t("issues.unchecked")}</div>;
    return (flow.templateIssues || []).map((issue) => (
      <div key={`${issue.step}-${issue.templateName}`} className={`${classes.warnText} ${own.issue}`}>
        <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
        {t("issues.line", { step: issueStep(issue.step), name: issue.templateName, status: issueStatus(issue.status) })}
      </div>
    ));
  };

  const renderRow = (flow) => {
    const meta = [
      flow.whatsappName,
      windowText(flow.sendWindow, t),
      flow.openRuns ? (flow.openRuns === 1 ? t("openRunsOne") : t("openRunsMany", { count: flow.openRuns })) : null,
    ].filter(Boolean);
    return (
      <div key={flow.id} className={classes.row} data-flow={flow.id}>
        <div
          className={`${classes.rowMain} ${own.rowMain} ${canManage ? "" : own.rowReadOnly}`}
          {...(canManage
            ? {
                role: "button",
                tabIndex: 0,
                onClick: () => setEditing({ flow }),
                onKeyDown: (ev) => {
                  if (ev.key === "Enter") setEditing({ flow });
                },
              }
            : {})}
        >
          <div className={classes.rowTitle}>
            {flow.name}
            <span className={`${classes.chip} ${flow.active ? own.chipActive : ""}`}>{flow.active ? t("active") : t("inactive")}</span>
          </div>
          <div className={`${classes.rowDesc} ${own.rowDesc}`}>
            {`${flow.initialTemplateName} (${flow.initialTemplateLanguage}) · ${
              flow.totalMessages === 1 ? t("messagesOne") : t("messagesMany", { count: flow.totalMessages })
            }: ${sequenceText(flow, sequenceLabel)}`}
          </div>
          <div className={classes.rowMeta}>{meta.join(" · ")}</div>
          {renderIssues(flow)}
        </div>
        {canManage && (
          <div className={`${classes.rowActions} ${own.rowActions}`}>
            <Tooltip title={flow.active ? t("deactivate") : t("activate")}>
              <Switch
                color="primary"
                size="small"
                checked={flow.active}
                disabled={busy}
                onChange={(ev) => toggle(flow, ev.target.checked)}
                inputProps={{ "aria-label": flow.active ? t("deactivate") : t("activate") }}
              />
            </Tooltip>
            <Tooltip title={t("edit")}>
              <button type="button" className={classes.iconBtn} onClick={() => setEditing({ flow })} aria-label={t("edit")}>
                <Pencil />
              </button>
            </Tooltip>
            <Tooltip title={t("delete")}>
              <button
                type="button"
                className={classes.iconBtn}
                onClick={() => setConfirm({ kind: "delete", flow })}
                aria-label={t("delete")}
                disabled={busy}
              >
                <Trash2 />
              </button>
            </Tooltip>
          </div>
        )}
      </div>
    );
  };

  const renderList = () => {
    if (loadFailed) return <LoadError message={t("loadError")} onRetry={load} />;
    if (flows === null) return <LoadingState />;
    if (!flows.length) {
      return (
        <div className={classes.empty}>
          <div className={classes.emptyTitle}>{t("empty.title")}</div>
          <div>{t("empty.text")}</div>
        </div>
      );
    }
    if (!visible.length) return <div className={classes.empty}>{t("empty.filtered")}</div>;
    return <div className={classes.rows}>{visible.map(renderRow)}</div>;
  };

  return (
    <MainContainer>
      <div className={own.page}>
        <div className={own.header}>
          <div>
            <div className={own.title}>
              <Sparkles size={22} strokeWidth={1.75} />
              {t("title")}
            </div>
            <div className={own.subtitle}>{t("subtitle")}</div>
          </div>
          {canManage && (
            <button
              type="button"
              className={classes.btnPrimary}
              onClick={() => setEditing({ flow: null })}
              disabled={connectionsLoading || !connections.length}
            >
              <Plus /> {t("newFlow")}
            </button>
          )}
        </div>

        {!canView ? (
          <div className={own.banner}>{t("onlyAdmins")}</div>
        ) : (
          <>
            {!connectionsLoading && !connections.length && <div className={own.banner}>{t("noConnection")}</div>}
            <div className={own.filters} role="group" aria-label={t("filtersLabel")}>
              {FILTERS.map((key) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={filter === key}
                  className={`${own.filterBtn} ${filter === key ? own.filterBtnOn : ""}`}
                  onClick={() => setFilter(key)}
                >
                  {t(`filters.${key}`)} <span className={own.filterCount}>{counts[key]}</span>
                </button>
              ))}
            </div>
            {renderList()}
          </>
        )}
      </div>

      {editing && (
        <FlowEditor
          open
          flow={editing.flow}
          connections={connections}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      <ConfirmationModal
        title={confirm?.kind === "delete" ? t("confirmDeleteTitle") : t("confirmDeactivateTitle")}
        open={!!confirm}
        onClose={() => {
          if (!busy) setConfirm(null);
        }}
        onConfirm={() => (confirm?.kind === "delete" ? remove(confirm.flow) : setActive(confirm.flow, false))}
        confirmLoading={busy}
        confirmLabel={confirm?.kind === "delete" ? t("delete") : t("deactivate")}
      >
        {confirm?.kind === "delete"
          ? t("confirmDeleteText")
          : t("confirmDeactivateText", { count: confirm?.flow?.openRuns || 0 })}
      </ConfirmationModal>
    </MainContainer>
  );
};

export default SmartApproaches;
